/**
 * PortVlanPanel -- what VLANs a switch port carries NOW, and an editor that starts from them.
 *
 * Owner, 2026-10-07: "When you try to modify a port, you should be able to see the current VLANs
 * so that you know what to add and/or remove." The panel used to print the PVID ("VLAN 1") and
 * offer one "Access VLAN" box, which on a trunk was a blind overwrite.
 *
 * READ SIDE (every role): mode with its reason, the untagged VLAN, the tagged VLANs as chips
 * (number + site name; a filled dot = traffic seen on it now), where each fact came from and how
 * old it is, live spanning tree, and a drift badge when the switch disagrees with Meridian.
 *
 * EDIT SIDE (msp_admin, rendered by the caller inside its RoleGate): Trunk / Access, the untagged
 * VLAN, the tagged chips with remove (struck through, undoable) and "Add VLAN" from the site's
 * VLANs or any id. The change line under it states the delta (+ added, - removed, untagged
 * a -> b) and warnings come from lib/portVlans before the preview; the preview then shows the
 * controller's own before -> after (VlanDiffList) next to the exact commands.
 *
 * DEVICE READ (OP#1819): SwitchVlanReadBar says where the switch's port VLANs come from -- read
 * from the switch itself (and how long ago), untagged-only (SonicFi: no CLI path), or unavailable
 * -- and offers "Read VLANs from switch" (one read-only CLI session, org_admin and up). Every
 * other role sees the result of the last read; nobody is left to assume a native-only view is
 * complete.
 *
 * Design system only: ink/surface/line tokens, healthy/warn/critical ramps, light + dark,
 * 44px touch targets, wraps inside the ~370px device sheet.
 */
import { useState } from "react";
import { cn } from "@/lib/cn";
import { ApiError } from "@/lib/api/client";
import { useReadPortVlans } from "@/lib/api/hooks";
import { RoleGate } from "@/components/auth/RoleGate";
import { Button } from "@/components/ui/Button";
import {
  deviceReadLine,
  fmtAge,
  modeLabel,
  parseVid,
  sourceLabel,
  splitDrift,
  vlanDelta,
  vlanLabel,
  type PortVlanDiff,
  type PortVlanView,
  type PortVlansResponse,
  type DeviceReadSummary,
  type VlanEdit,
} from "@/lib/portVlans";

/** The server's words for a failed read (409 SonicFi / CLI off / busy, 429, 502), else generic. */
export function readErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    if (body?.error) return body.error;
    return `Read failed (HTTP ${err.status}).`;
  }
  return "Read failed.";
}

/**
 * Where this switch's port VLANs come from, and the "Read VLANs from switch" action. Renders for
 * every role; the button only for org_admin and up (the CLI-relay floor) and only when the
 * controller says a CLI read is possible (never on a SonicFi/CyberTan RSW).
 */
export function SwitchVlanReadBar({
  serial,
  readSummary,
  loading,
}: {
  serial: string;
  readSummary: DeviceReadSummary | null | undefined;
  loading?: boolean;
}) {
  const read = useReadPortVlans();
  const line = deviceReadLine(readSummary);
  const tone = line.tone === "ok" ? "text-ink-muted" : line.tone === "warn" ? "text-warn" : "text-ink-subtle";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2" data-testid="switch-vlan-read">
      <p className={cn("min-w-0 flex-1 text-[11px] leading-snug", tone)}>
        {loading ? "Reading this switch's VLAN sources…" : line.text}
      </p>
      {readSummary?.can_read && (
        <RoleGate min="org_admin" mode="hide">
          <Button
            variant="secondary"
            onClick={() => read.mutate(serial)}
            disabled={read.isPending}
            title="One read-only CLI session: show vlan + show interfaces switchport. Nothing is written to the switch."
          >
            {read.isPending ? "Reading switch…" : "Read VLANs from switch"}
          </Button>
        </RoleGate>
      )}
      {read.isError && (
        <p role="alert" className="w-full text-[11px] text-critical">{readErrorText(read.error)}</p>
      )}
    </div>
  );
}

/** One VLAN chip. `struck` = being removed by the edit; `added` = being added. */
function VlanChip({
  vlan,
  name,
  seen,
  struck,
  added,
  onToggle,
  title,
}: {
  vlan: number;
  name?: string | null;
  seen?: boolean;
  struck?: boolean;
  added?: boolean;
  onToggle?: () => void;
  title?: string;
}) {
  const body = (
    <>
      {seen !== undefined && (
        // Filled = traffic seen now; hollow = none seen (a quiet VLAN is not a missing VLAN).
        <span
          aria-hidden
          className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full border",
            seen ? "border-healthy bg-healthy" : "border-ink-subtle bg-transparent")}
        />
      )}
      <span className="font-mono tabular-nums">{vlan}</span>
      {name && <span className="truncate text-ink-muted">{name}</span>}
      {onToggle && <span aria-hidden className="text-ink-subtle">{struck ? "↺" : "×"}</span>}
    </>
  );
  const cls = cn(
    "inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]",
    added ? "border-healthy bg-healthy-soft text-ink"
      : struck ? "border-critical/60 bg-critical-soft text-ink-muted line-through"
        : "border-line bg-surface text-ink",
  );
  if (!onToggle) return <span className={cls} title={title}>{body}</span>;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(cls, "min-h-[32px] hover:border-ink-subtle")}
      title={title}
      aria-label={struck ? `Keep VLAN ${vlan}` : `Remove VLAN ${vlan}`}
      aria-pressed={struck}
    >
      {body}
    </button>
  );
}

/** Read-only facts: mode, untagged, tagged, STP, sources, drift. */
export function PortVlanFacts({
  view,
  sources,
  loading,
  error,
}: {
  view: PortVlanView | null;
  sources?: PortVlansResponse["sources"];
  loading?: boolean;
  error?: boolean;
}) {
  if (loading) return <p className="text-xs text-ink-muted">Reading this port's VLANs…</p>;
  if (error || !view) {
    return (
      <p className="text-xs text-ink-muted">
        This port's VLANs could not be read. The untagged VLAN in the line above is the switch's own
        report; tagged VLANs are not known here.
      </p>
    );
  }
  const { drift, unconfirmed, info } = splitDrift(view);
  const live = sources?.live;
  const applied = sources?.applied;
  return (
    <div className="flex flex-col gap-2">
      {drift.length > 0 && (
        <div role="status" className="rounded border border-warn bg-warn-soft p-2 text-xs text-warn">
          <div className="font-medium">Switch differs from Meridian</div>
          <ul className="mt-0.5 list-disc pl-4">
            {drift.map((d) => <li key={d.code}>{d.message}</li>)}
          </ul>
        </div>
      )}
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-ink-muted">Mode</dt>
        <dd className="min-w-0 text-ink">
          <span className="font-medium">{modeLabel(view.mode)}</span>
          <span className="text-ink-subtle"> · {view.mode_reason}</span>
        </dd>
        <dt className="text-ink-muted">Untagged</dt>
        <dd className="min-w-0 text-ink">
          {view.native.vlan != null ? (
            <span className="font-mono tabular-nums">{vlanLabel(view.native.vlan, view.native.name)}</span>
          ) : (
            <span className="text-ink-subtle">not reported</span>
          )}
          <span className="text-ink-subtle"> · {sourceLabel(view.native.source)}</span>
        </dd>
        <dt className="text-ink-muted">Tagged</dt>
        <dd className="flex min-w-0 flex-wrap gap-1">
          {view.tagged.length > 0 ? (
            view.tagged.map((t) => (
              <VlanChip
                key={t.vlan}
                vlan={t.vlan}
                name={t.name}
                seen={t.seen}
                title={`${vlanLabel(t.vlan, t.name)} — ${t.seen ? "traffic seen now" : "no traffic seen now"}${t.intended ? " · in the site VLAN plan" : ""}`}
              />
            ))
          ) : (
            <span className="text-ink-subtle">
              {view.tagged_known ? "none" : "not recorded — the switch does not report tagged VLANs"}
            </span>
          )}
        </dd>
        <dt className="text-ink-muted">Spanning tree</dt>
        <dd className="min-w-0 text-ink">
          Edge {view.stp.edge == null ? "unknown" : view.stp.edge ? "yes" : "no"}
          {view.stp.role && <span className="text-ink-muted"> · {view.stp.role}</span>}
          {view.stp.state && <span className="text-ink-muted"> · {view.stp.state}</span>}
        </dd>
      </dl>
      <p className="text-[11px] leading-snug text-ink-subtle">
        Untagged and traffic from the switch report{live?.age_seconds != null ? ` (${fmtAge(live.age_seconds)})` : ""}.
        {" "}Tagged from {sourceLabel(view.tagged_source)}
        {view.tagged_source === "cli" && view.observed?.age_seconds != null ? ` ${fmtAge(view.observed.age_seconds)}` : ""}
        {view.observed?.confidence && view.observed.confidence !== "high" && view.observed.note ? ` (${view.observed.note})` : ""}
        {view.tagged_source === "applied" && view.applied_at ? ` (last ${view.applied_at.slice(0, 10)})` : ""}
        {view.tagged_source === "applied" && applied?.sessions ? `, ${applied.sessions} recorded change${applied.sessions === 1 ? "" : "s"}` : ""}.
        {view.tagged.length > 0 && " ● traffic seen now · ○ none seen."}
      </p>
      {unconfirmed.length > 0 && (
        <p className="text-[11px] leading-snug text-ink-muted">{unconfirmed.map((d) => d.message).join(" ")}</p>
      )}
      {info.length > 0 && (
        <p className="text-[11px] leading-snug text-ink-muted">{info.map((d) => d.message).join(" ")}</p>
      )}
    </div>
  );
}

/** The VLAN editor. Controlled: the caller owns `edit` (null = not editing). */
export function PortVlanEditor({
  view,
  edit,
  setEdit,
  siteVlans,
}: {
  view: PortVlanView;
  edit: VlanEdit;
  setEdit: (next: VlanEdit) => void;
  siteVlans: { vlan: number; name: string | null }[];
}) {
  const [addText, setAddText] = useState("");
  const names = new Map<number, string | null>(siteVlans.map((s) => [s.vlan, s.name]));
  for (const t of view.tagged) if (t.name) names.set(t.vlan, t.name);
  const current = new Set(view.tagged.map((t) => t.vlan));
  const kept = new Set(edit.tagged);
  const delta = vlanDelta(view, edit);
  const untaggedVid = parseVid(edit.untagged);
  const addable = siteVlans.filter((s) => !kept.has(s.vlan) && s.vlan !== untaggedVid);
  const addVid = parseVid(addText);

  const toggle = (vid: number) =>
    setEdit({ ...edit, tagged: kept.has(vid) ? edit.tagged.filter((x) => x !== vid) : [...edit.tagged, vid].sort((a, b) => a - b) });
  const add = (vid: number | null) => {
    if (vid == null || kept.has(vid)) return;
    setEdit({ ...edit, tagged: [...edit.tagged, vid].sort((a, b) => a - b) });
    setAddText("");
  };

  // Chips: every current VLAN (struck when removed) plus every newly added one.
  const chips = [...new Set([...current, ...edit.tagged])].sort((a, b) => a - b);
  const inputCls = "min-h-touch rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink tabular-nums";

  return (
    <fieldset className="flex flex-col gap-2 rounded border border-line bg-surface p-2">
      <legend className="px-1 text-xs font-medium text-ink">VLANs on this port</legend>
      <div role="radiogroup" aria-label="Port mode" className="grid grid-cols-2 gap-1">
        {(["trunk", "access"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={edit.mode === m}
            onClick={() => setEdit({ ...edit, mode: m })}
            className={cn(
              "min-h-touch rounded border text-sm",
              edit.mode === m ? "border-brand bg-brand-soft font-medium text-ink" : "border-line text-ink-muted hover:text-ink",
            )}
          >
            {m === "trunk" ? "Trunk (tagged + untagged)" : "Access (one VLAN)"}
          </button>
        ))}
      </div>

      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        {edit.mode === "access" ? "VLAN" : "Untagged (native) VLAN"}
        <input
          className={inputCls}
          inputMode="numeric"
          value={edit.untagged}
          placeholder={view.native.vlan != null ? String(view.native.vlan) : "1–4094"}
          onChange={(e) => setEdit({ ...edit, untagged: e.target.value })}
          list="port-vlan-site-vlans"
        />
      </label>
      <datalist id="port-vlan-site-vlans">
        {siteVlans.map((s) => <option key={s.vlan} value={s.vlan}>{s.name ?? ""}</option>)}
      </datalist>

      {edit.mode === "trunk" && (
        <div className="flex flex-col gap-1 text-xs text-ink-muted">
          <span>Tagged VLANs <span className="text-ink-subtle">— tap one to remove it</span></span>
          <div className="flex flex-wrap gap-1">
            {chips.length === 0 && <span className="text-ink-subtle">none</span>}
            {chips.map((vid) => (
              <VlanChip
                key={vid}
                vlan={vid}
                name={names.get(vid)}
                struck={current.has(vid) && !kept.has(vid)}
                added={!current.has(vid) && kept.has(vid)}
                onToggle={() => toggle(vid)}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {addable.length > 0 && (
              <select
                className={cn(inputCls, "min-w-0 flex-1")}
                value=""
                aria-label="Add a site VLAN"
                onChange={(e) => add(parseVid(e.target.value))}
              >
                <option value="">Add site VLAN…</option>
                {addable.map((s) => <option key={s.vlan} value={s.vlan}>{vlanLabel(s.vlan, s.name)}</option>)}
              </select>
            )}
            <input
              className={cn(inputCls, "w-24")}
              inputMode="numeric"
              placeholder="VLAN id"
              aria-label="Add VLAN by id"
              value={addText}
              onChange={(e) => setAddText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(addVid); } }}
            />
            <button
              type="button"
              disabled={addVid == null || kept.has(addVid)}
              onClick={() => add(addVid)}
              className="min-h-touch rounded border border-line px-3 text-sm text-ink disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      <ChangeLine delta={delta} names={names} />
    </fieldset>
  );
}

/** "+ 601 · − 400 · untagged 1 → 400", then errors and warnings. */
function ChangeLine({ delta, names }: { delta: ReturnType<typeof vlanDelta>; names: Map<number, string | null> }) {
  const none = !delta.added.length && !delta.removed.length && !delta.nativeChange;
  return (
    <div className="flex flex-col gap-1 text-[11px]">
      <div className="text-ink-muted">
        {none ? (
          "No VLAN change."
        ) : (
          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
            <span className="text-ink">Change:</span>
            {delta.added.map((v) => <span key={`a${v}`} className="text-healthy">+ {vlanLabel(v, names.get(v))}</span>)}
            {delta.removed.map((v) => <span key={`r${v}`} className="text-critical">− {vlanLabel(v, names.get(v))}</span>)}
            {delta.nativeChange && (
              <span className="text-ink">untagged {delta.nativeChange.from ?? "?"} → {delta.nativeChange.to}</span>
            )}
          </span>
        )}
      </div>
      {delta.errors.map((e) => <span key={e} className="text-critical">{e}</span>)}
      {delta.warnings.map((w) => (
        <span key={w} className="rounded border border-warn bg-warn-soft px-2 py-1 text-warn">{w}</span>
      ))}
    </div>
  );
}

/** The controller's before -> after for a preview (one block per port). */
export function VlanDiffList({ diffs }: { diffs: PortVlanDiff[] | null | undefined }) {
  if (!diffs || diffs.length === 0) return null;
  return (
    <div className="mb-2 flex flex-col gap-1.5">
      {diffs.map((d) => (
        <div key={d.port} className="text-[11px] text-ink-muted">
          <div>
            <span className="text-ink">Before</span> untagged {d.before.native ?? "?"} · tagged{" "}
            {d.before.tagged.length ? d.before.tagged.join(", ") : "none recorded"}
          </div>
          <div>
            <span className="text-ink">After</span> untagged {d.after.native ?? "?"} · tagged{" "}
            {d.after.tagged.length ? d.after.tagged.join(", ") : "none"}
          </div>
          <div className="flex flex-wrap gap-x-2">
            {d.added.map((v) => <span key={`a${v}`} className="text-healthy">+ {v}</span>)}
            {d.removed.map((v) => <span key={`r${v}`} className="text-critical">− {v}</span>)}
            {d.native_change && <span className="text-ink">untagged {d.native_change.from ?? "?"} → {d.native_change.to ?? "?"}</span>}
            {!d.added.length && !d.removed.length && !d.native_change && <span>No VLAN change.</span>}
          </div>
          {d.warnings.map((w) => (
            <div key={w} className="mt-0.5 rounded border border-warn bg-warn-soft px-2 py-1 text-warn">{w}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
